import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ShoppingCart, Phone, Check, ChevronLeft, ChevronRight, MessageSquare, Info, ChevronDown } from 'lucide-react';
import { categories } from '../data/products';
import { useCart } from '../components/CartContext';
import { QuoteModal } from '../components/QuoteModal';
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-1733">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-brand-lightgray/50 dark:bg-[#1a1a1a] border border-stone-200 dark:border-[#555555] rounded-none text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all flex items-center justify-between font-medium ${value ? '' : 'text-stone-400'}`} data-id="element-1734">
        <span className="truncate" data-id="element-1735">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1736" />
      </button>

      <AnimatePresence data-id="element-1737">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full bottom-full mb-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-white/10 rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-1738">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-brand-orange/20 text-brand-orange' : 'text-brand-charcoal dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-1739">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const ProductImageCarousel = ({
  images,
  alt
}: {
  images: string[];
  alt: string;
}) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
  }, [images]);
  if (!images || images.length <= 1) {
    return <img src={images[0]} alt={alt} className="absolute inset-0 w-full h-full object-cover" data-id="element-1740" />;
  }
  return <div className="absolute inset-0 w-full h-full group" data-id="element-1741">
      <img src={images[idx]} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" data-id="element-1742" />
      <div className="absolute inset-0 flex items-center justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1743">
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === 0 ? images.length - 1 : i - 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-1744">
          <ChevronLeft className="w-5 h-5" data-id="element-1745" />
        </button>
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === images.length - 1 ? 0 : i + 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-1746">
          <ChevronRight className="w-5 h-5" data-id="element-1747" />
        </button>
      </div>
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2" data-id="element-1748">
        {images.map((_, i) => <button key={i} onClick={e => {
        e.stopPropagation();
        setIdx(i);
      }} className={`w-2 h-2 rounded-full transition-colors ${i === idx ? 'bg-white' : 'bg-white/50 hover:bg-white/75'}`} data-id="element-1749" />)}
      </div>
    </div>;
};
export function Product() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    addToCart
  } = useCart();
  const [quantity, setQuantity] = useState<string>('12');
  const [numLoads, setNumLoads] = useState<string>('');
  const [addedFlash, setAddedFlash] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  // Build flat list of all products for prev/next navigation
  const allProducts = useMemo(() => {
    const list: {
      slug: string;
      name: string;
      categoryName: string;
    }[] = [];
    for (const cat of categories) {
      for (const prod of cat.products) {
        list.push({
          slug: prod.slug,
          name: prod.name,
          categoryName: cat.name
        });
      }
    }
    return list;
  }, []);
  // Find product and its category
  let product = null;
  let category = null;
  for (const cat of categories) {
    const found = cat.products.find(p => p.slug === slug);
    if (found) {
      product = found;
      category = cat;
      break;
    }
  }
  const currentIndex = allProducts.findIndex(p => p.slug === slug);
  const prevProduct = currentIndex > 0 ? allProducts[currentIndex - 1] : null;
  const nextProduct = currentIndex < allProducts.length - 1 ? allProducts[currentIndex + 1] : null;
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!product || !category) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-id="element-1750">
        <h1 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-1751">
          Product not found
        </h1>
        <button onClick={() => navigate('/')} className="text-brand-orange hover:underline" data-id="element-1752">
          Return to Home
        </button>
      </div>;
  }
  const isSpecialty = category.name === 'Specialty Rock';
  const isBark = product.name.toLowerCase().includes('bark');
  const isLimitedTo12 = product.name === 'Builders Sand' || product.name === 'Fill Dirt';
  const handleAddToCart = () => {
    if (quantity === 'multi' && numLoads === '3+') {
      setIsQuoteModalOpen(true);
      return;
    }
    let finalQuantity = Number(quantity);
    if (quantity === 'multi') {
      const deliveries = Number(numLoads);
      const fullLoadYards = isBark ? 15 : 12;
      finalQuantity = fullLoadYards * deliveries;
    }
    addToCart({
      category: category.name,
      product: product.name,
      quantity: finalQuantity,
      pricePerYard: product.pricePerYard
    });
    setAddedFlash(true);
    setTimeout(() => setAddedFlash(false), 2000);
  };
  // Handle multi-load custom quote redirect
  useEffect(() => {
    if (quantity === 'multi' && numLoads === '3+') {
      setIsQuoteModalOpen(true);
      setNumLoads('');
      setQuantity(isBark ? '15' : '12');
    }
  }, [quantity, numLoads, isBark]);
  return <main className="py-12 md:py-20 bg-[#1b1b1b] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)] transition-colors duration-300" data-id="element-1753">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1754">
        {/* Top nav: Back + Prev/Next */}
        <div className="flex items-center justify-between mb-8" data-id="element-1755">
          <Link to="/#materials" className="inline-flex items-center text-sm font-medium text-brand-muted hover:text-brand-orange transition-colors" data-id="element-1756">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1757" />
            Back to Materials
          </Link>
          <div className="flex items-center gap-2" data-id="element-1758">
            {prevProduct ? <Link to={`/shop/${prevProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={prevProduct.name} data-id="element-1759">
                <ChevronLeft className="w-4 h-4" data-id="element-1760" />
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-1761">
                  {prevProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-1762">Prev</span>
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-1763">
                <ChevronLeft className="w-4 h-4" data-id="element-1764" />
              </span>}
            {nextProduct ? <Link to={`/shop/${nextProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-white dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={nextProduct.name} data-id="element-1765">
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-1766">
                  {nextProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-1767">Next</span>
                <ChevronRight className="w-4 h-4" data-id="element-1768" />
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-1769">
                <ChevronRight className="w-4 h-4" data-id="element-1770" />
              </span>}
          </div>
        </div>

        <div className="bg-white dark:bg-[#333333] rounded-2xl border border-stone-200 dark:border-[#444444] overflow-hidden shadow-sm flex flex-col md:flex-row" data-id="element-1771">
          {/* Image */}
          <div className="w-full md:w-1/2 h-64 md:h-auto relative" data-id="element-1772">
            <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-1773" />
          </div>

          {/* Content */}
          <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-10 flex flex-col" data-id="element-1774">
            <div className="mb-2" data-id="element-1775">
              <span className="text-xs font-bold text-brand-muted uppercase tracking-wider" data-id="element-1776">
                {category.name}
              </span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-1777">
              {product.name}
            </h1>
            <p className="text-sm text-brand-charcoal/80 dark:text-white/80 mb-6 leading-relaxed" data-id="element-1778">
              {product.description}
            </p>

            {/* Product Specs & Info */}
            <div className="mb-8 space-y-6" data-id="element-1779">
              {product.bestFor && product.bestFor.length > 0 && <div data-id="element-1780">
                  <h3 className="text-xs font-bold text-brand-charcoal dark:text-white uppercase tracking-wider mb-3" data-id="element-1781">
                    Best For
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-1782">
                    {product.bestFor.map((tag, idx) => <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full bg-brand-lightgray dark:bg-[#2a2a2a] text-xs font-medium text-brand-charcoal dark:text-stone-300 border border-stone-200 dark:border-[#444444]" data-id="element-1783">
                        {tag}
                      </span>)}
                  </div>
                </div>}

              {product.specs && <div data-id="element-1784">
                  <h3 className="text-xs font-bold text-brand-charcoal dark:text-white uppercase tracking-wider mb-3" data-id="element-1785">
                    Specifications
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1786">
                    {product.specs.size && <div className="bg-brand-lightgray/30 dark:bg-[#222222] p-3 rounded-lg border border-stone-100 dark:border-[#333333]" data-id="element-1787">
                        <p className="text-[10px] text-brand-muted dark:text-stone-500 uppercase tracking-wider mb-1" data-id="element-1788">
                          Size
                        </p>
                        <p className="text-sm font-medium text-brand-charcoal dark:text-stone-200" data-id="element-1789">
                          {product.specs.size}
                        </p>
                      </div>}
                    {product.specs.weightPerYard && <div className="bg-brand-lightgray/30 dark:bg-[#222222] p-3 rounded-lg border border-stone-100 dark:border-[#333333]" data-id="element-1790">
                        <p className="text-[10px] text-brand-muted dark:text-stone-500 uppercase tracking-wider mb-1" data-id="element-1791">
                          Weight / Yard
                        </p>
                        <p className="text-sm font-medium text-brand-charcoal dark:text-stone-200" data-id="element-1792">
                          {product.specs.weightPerYard}
                        </p>
                      </div>}
                    {product.specs.compaction && <div className="bg-brand-lightgray/30 dark:bg-[#222222] p-3 rounded-lg border border-stone-100 dark:border-[#333333]" data-id="element-1793">
                        <p className="text-[10px] text-brand-muted dark:text-stone-500 uppercase tracking-wider mb-1" data-id="element-1794">
                          Compaction
                        </p>
                        <p className="text-sm font-medium text-brand-charcoal dark:text-stone-200" data-id="element-1795">
                          {product.specs.compaction}
                        </p>
                      </div>}
                    {product.specs.coverage && <div className="bg-brand-lightgray/30 dark:bg-[#222222] p-3 rounded-lg border border-stone-100 dark:border-[#333333]" data-id="element-1796">
                        <p className="text-[10px] text-brand-muted dark:text-stone-500 uppercase tracking-wider mb-1" data-id="element-1797">
                          Coverage
                        </p>
                        <p className="text-sm font-medium text-brand-charcoal dark:text-stone-200" data-id="element-1798">
                          {product.specs.coverage}
                        </p>
                      </div>}
                  </div>
                </div>}

              {product.proTip && <div className="bg-brand-orange/10 border border-brand-orange/20 p-4 rounded-xl flex items-start gap-3" data-id="element-1799">
                  <div className="bg-brand-orange/20 p-1.5 rounded-full shrink-0 mt-0.5" data-id="element-1800">
                    <Check className="w-4 h-4 text-brand-orange" data-id="element-1801" />
                  </div>
                  <div data-id="element-1802">
                    <h4 className="text-sm font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-1803">
                      Pro Tip
                    </h4>
                    <p className="text-sm text-brand-charcoal/80 dark:text-stone-300 leading-relaxed" data-id="element-1804">
                      {product.proTip}
                    </p>
                  </div>
                </div>}
            </div>

            <div className="mt-auto" data-id="element-1805">
              {isSpecialty ? <div className="space-y-4" data-id="element-1806">
                  <div className="p-4 bg-brand-lightgray/50 dark:bg-[#222222] rounded-xl border border-stone-200 dark:border-[#444444]" data-id="element-1807">
                    <p className="text-sm text-brand-charcoal dark:text-stone-300" data-id="element-1808">
                      Pricing and availability for specialty rock varies. Please
                      contact us directly for a custom quote.
                    </p>
                  </div>
                  <a href="tel:206-619-3742" className="w-full bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal hover:bg-brand-charcoal/90 dark:hover:bg-stone-200 py-4 rounded-xl font-bold text-lg transition-colors flex items-center justify-center" data-id="element-1809">
                    <Phone className="w-5 h-5 mr-2" data-id="element-1810" />
                    Call for Pricing
                  </a>
                </div> : <div className="space-y-6" data-id="element-1811">
                  <div className="flex items-end justify-between border-b border-stone-100 dark:border-[#444444] pb-6" data-id="element-1812">
                    <div data-id="element-1813">
                      <p className="text-sm text-brand-muted dark:text-stone-500 font-medium mb-1" data-id="element-1814">
                        Price
                      </p>
                      {(() => {
                    const fullLoad = isBark ? 15 : 12;
                    const totalYards = quantity === 'multi' && numLoads && numLoads !== '3+' ? Number(numLoads) * fullLoad : quantity !== 'multi' ? Number(quantity) : 0;
                    const fullLoadSize = isBark ? 15 : 12;
                    const bulkPct = totalYards >= fullLoadSize * 4 ? 0.1 : totalYards >= fullLoadSize * 2 ? 0.05 : 0;
                    const discountedPrice = bulkPct > 0 ? Math.ceil(product.pricePerYard * (1 - bulkPct)) : product.pricePerYard;
                    return <>
                            <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white" data-id="element-1815">
                              ${discountedPrice}{' '}
                              <span className="text-base font-normal text-brand-muted dark:text-stone-500" data-id="element-1816">
                                / yard
                              </span>
                              {bulkPct > 0 && <span className="text-sm font-semibold text-green-600 dark:text-green-400 ml-2" data-id="element-1817">
                                  {Math.round(bulkPct * 100)}% bulk discount
                                </span>}
                            </p>
                            {bulkPct > 0 && <p className="text-sm text-brand-muted dark:text-stone-500 mt-1" data-id="element-1818">
                                <span className="line-through" data-id="element-1819">
                                  ${product.pricePerYard}/yd
                                </span>
                                <span className="text-green-600 dark:text-green-400 ml-2 font-medium" data-id="element-1820">
                                  Save $
                                  {(product.pricePerYard - discountedPrice) * totalYards}{' '}
                                  on {totalYards} yards
                                </span>
                              </p>}
                          </>;
                  })()}
                    </div>
                  </div>

                  <div className="space-y-4" data-id="element-1821">
                    <label className="block text-sm font-bold text-brand-charcoal dark:text-stone-300" data-id="element-1822">
                      Select Quantity
                    </label>
                    <div className="flex flex-col sm:flex-row gap-4" data-id="element-1823">
                      <div className="w-full sm:w-1/2" data-id="element-1824">
                        <CustomSelect value={quantity} onChange={val => {
                      setQuantity(val);
                      if (val !== 'multi') setNumLoads('');
                    }} placeholder="Quantity" options={isBark ? [{
                      value: '7',
                      label: '7 Yards'
                    }, {
                      value: '8',
                      label: '8 Yards'
                    }, {
                      value: '9',
                      label: '9 Yards'
                    }, {
                      value: '10',
                      label: '10 Yards'
                    }, {
                      value: '11',
                      label: '11 Yards'
                    }, {
                      value: '12',
                      label: '12 Yards'
                    }, {
                      value: '13',
                      label: '13 Yards'
                    }, {
                      value: '14',
                      label: '14 Yards'
                    }, {
                      value: '15',
                      label: '15 Yards (Full Load)'
                    }, {
                      value: 'multi',
                      label: 'More than 1 load'
                    }] : [{
                      value: '6',
                      label: '6 Yards'
                    }, {
                      value: '7',
                      label: '7 Yards'
                    }, {
                      value: '8',
                      label: '8 Yards'
                    }, {
                      value: '9',
                      label: '9 Yards'
                    }, {
                      value: '10',
                      label: '10 Yards'
                    }, {
                      value: '11',
                      label: '11 Yards'
                    }, {
                      value: '12',
                      label: '12 Yards (Full Load)'
                    }, {
                      value: 'multi',
                      label: 'More than 1 load'
                    }]} data-id="element-1825" />

                        <AnimatePresence data-id="element-1826">
                          {quantity === 'multi' && <motion.div initial={{
                        opacity: 0
                      }} animate={{
                        opacity: 1
                      }} exit={{
                        opacity: 0
                      }} transition={{
                        duration: 0.2
                      }} className="overflow-visible mt-3" data-id="element-1827">
                              <CustomSelect value={numLoads} onChange={val => setNumLoads(val)} placeholder="How many loads?" options={[{
                          value: '2',
                          label: `2 Loads (${isBark ? '30' : '24'} yds)`
                        }, {
                          value: '3',
                          label: `3 Loads (${isBark ? '45' : '36'} yds)`
                        }, {
                          value: '4',
                          label: `4 Loads (${isBark ? '60' : '48'} yds)`
                        }, {
                          value: '3+',
                          label: '5+ Loads (Custom Quote)'
                        }]} data-id="element-1828" />
                            </motion.div>}
                        </AnimatePresence>
                      </div>

                      {addedFlash ? <motion.div initial={{
                    opacity: 0,
                    scale: 0.95
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} className="flex-1 flex items-center justify-center bg-[#76cc26]/10 dark:bg-[#76cc26]/10 text-[#76cc26] dark:text-[#76cc26] rounded-none font-bold border border-[#76cc26]/30 dark:border-[#76cc26]/30 py-3 sm:py-0" data-id="element-1829">
                          <Check className="w-5 h-5 mr-2" data-id="element-1830" />
                          Added to Cart
                        </motion.div> : <button onClick={handleAddToCart} disabled={quantity === 'multi' && !numLoads} className={`flex-1 py-3 rounded-none font-bold transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${quantity && quantity !== 'multi' ? 'bg-brand-button hover:bg-brand-buttonHover text-white shadow-[0_0_12px_rgba(5,137,77,0.4)]' : quantity === 'multi' && numLoads && numLoads !== '3+' ? 'bg-brand-button hover:bg-brand-buttonHover text-white shadow-[0_0_12px_rgba(5,137,77,0.4)]' : 'bg-brand-charcoal dark:bg-white/10 text-white dark:text-white hover:bg-brand-charcoal/90 dark:hover:bg-white/15 border border-transparent dark:border-white/10'}`} data-id="element-1831">
                          <ShoppingCart className="w-5 h-5 mr-2" data-id="element-1832" />
                          Add to Cart
                        </button>}
                    </div>

                    <AnimatePresence data-id="element-1833">
                      {quantity === 'multi' && numLoads && numLoads !== '3+' && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="overflow-hidden" data-id="element-1834">
                            <div className="flex items-start gap-2 mt-3 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-1835">
                              <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-1836" />
                              <p className="text-xs text-brand-charcoal dark:text-stone-300" data-id="element-1837">
                                Multi-load orders require separate deliveries.
                                You will be charged a delivery fee per load.
                              </p>
                            </div>
                          </motion.div>}
                    </AnimatePresence>

                    {quantity === 'multi' && !numLoads && <div className="flex items-start gap-2 mt-3 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-1838">
                        <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-1839" />
                        <p className="text-xs text-brand-charcoal dark:text-stone-300" data-id="element-1840">
                          Please select how many loads you need.
                        </p>
                      </div>}
                  </div>

                  {quantity === 'multi' && numLoads === '3+' && <button onClick={() => setIsQuoteModalOpen(true)} className="w-full bg-brand-button hover:bg-brand-buttonHover text-white py-4 rounded-none font-bold text-lg transition-colors flex items-center justify-center" data-id="element-1841">
                      Get a Custom Quote
                    </button>}

                  {/* Custom amount / bulk note */}
                  <p className="text-xs text-brand-charcoal/70 dark:text-white/70 text-center" data-id="element-1842">
                    Need a custom amount?{' '}
                    <a href="tel:206-619-3742" className="text-brand-orange hover:underline" data-id="element-1843">
                      Call us
                    </a>{' '}
                    or{' '}
                    <Link to="/contact" className="text-brand-orange hover:underline" data-id="element-1844">
                      contact us
                    </Link>
                    .
                  </p>

                  {/* Helpful resource links */}
                  <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-2" data-id="element-1845">
                    <Link to="/compare" className="text-xs text-brand-charcoal/60 dark:text-white/50 hover:text-brand-orange dark:hover:text-brand-orange transition-colors underline underline-offset-2" data-id="element-1846">
                      Compare Materials
                    </Link>
                    <span className="text-xs text-brand-charcoal/30 dark:text-white/20" data-id="element-1847">
                      ·
                    </span>
                    <Link to="/help" className="text-xs text-brand-charcoal/60 dark:text-white/50 hover:text-brand-orange dark:hover:text-brand-orange transition-colors underline underline-offset-2" data-id="element-1848">
                      Quiz & Calculator
                    </Link>
                    <span className="text-xs text-brand-charcoal/30 dark:text-white/20" data-id="element-1849">
                      ·
                    </span>
                    <Link to="/diy-help" className="text-xs text-brand-charcoal/60 dark:text-white/50 hover:text-brand-orange dark:hover:text-brand-orange transition-colors underline underline-offset-2" data-id="element-1850">
                      DIY Help
                    </Link>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-1851" />
    </main>;
}